/**
 * Под-вкладка «Сцепки» (AK-747, форма 2 макета `machinery-forms`).
 *
 * Сцепка живёт на уровне КОМПАНИИ (у неё нет хозяйства), поэтому раздел
 * показывает агрегаты всей компании, а не только этого хозяйства — и говорит
 * об этом прямо. Место в навигации не меняем: раздел открывается там же, где
 * человек уже смотрит технику.
 *
 * Смысл экрана не «завести ещё один список», а чтобы сцепка нашла свои
 * нормативы: выработку за смену и расход топлива. Поэтому колонка «Норматив»
 * — главная, а «нет» всегда сопровождается причиной: у машины не заполнена
 * ширина захвата или в книге нет строки под такой агрегат.
 */
import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Plus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Stack } from '@/components/ui/stack';
import { toast } from '@/lib/toast';
import { UnitDialog } from './UnitDialog';
import {
  compositionLabel,
  normLabel,
  unitsSummary,
  widthLabel,
  type MachineryUnit,
} from './unitsLogic';
import { useMachineryUnits, useSaveUnit, useSetUnitOperations } from './unitsQueries';

export function MachineryUnitsPanel({
  farmId,
  canWrite,
}: {
  farmId: string;
  canWrite: boolean;
}) {
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  const query = useMachineryUnits(farmId, search);
  const page = query.data;
  const units = useMemo(() => page?.items ?? [], [page]);

  const saveUnit = useSaveUnit(farmId);
  const setOperations = useSetUnitOperations(farmId);

  return (
    <Stack gap={4}>
      <Card>
        <CardContent className="p-4 text-sm text-muted-foreground">
          Сцепка — <span className="font-medium text-foreground">тип агрегата</span>: «К-744 Р1 +
          КПС-8 ×2», собранный из марок каталога. Она принадлежит компании, а не
          хозяйству, поэтому список здесь общий — конкретные машины назначаются
          в момент операции. Выработку и расход топлива сцепка не хранит, а
          показывает: они живут нормативами.
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center gap-3">
        <Input
          value={search}
          placeholder="Поиск по названию сцепки"
          className="max-w-xs"
          onChange={(e) => setSearch(e.target.value)}
          data-testid="units-search"
        />
        <div className="flex-1" />
        {canWrite && (
          <Button size="sm" onClick={() => setDialogOpen(true)} data-testid="units-create">
            <Plus className="mr-2 h-4 w-4" />
            Собрать сцепку
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/50 text-xs uppercase text-muted-foreground">
                  <th className="w-10 p-3" />
                  <th className="p-3 text-left font-semibold">Сцепка</th>
                  <th className="p-3 text-left font-semibold">Состав</th>
                  <th className="p-3 text-left font-semibold">Ширина</th>
                  <th className="p-3 text-left font-semibold">Операции</th>
                  <th className="p-3 text-left font-semibold">Норматив</th>
                </tr>
              </thead>
              <tbody>
                {units.map((unit) => (
                  <UnitRow
                    key={unit.id}
                    unit={unit}
                    expanded={expanded === unit.id}
                    canWrite={canWrite}
                    onToggle={() => setExpanded(expanded === unit.id ? null : unit.id)}
                    onOperationsChange={async (ids) => {
                      try {
                        await setOperations.mutateAsync({ unit, operationIds: ids });
                      } catch {
                        toast.error('Не получилось сохранить набор операций — попробуйте ещё раз.');
                      }
                    }}
                  />
                ))}
                {!query.isLoading && units.length === 0 && (
                  <tr>
                    <td colSpan={6} className="p-6 text-center text-sm text-muted-foreground">
                      {search.trim()
                        ? 'Под фильтр ничего не попало.'
                        : 'Сцепок пока нет. Соберите первую — и станет видно, какие нормативы на неё есть.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="border-t border-border p-3 text-xs text-muted-foreground">
            {query.isLoading ? 'Загружаю…' : unitsSummary(page, units.length)}
          </div>
        </CardContent>
      </Card>

      <UnitDialog
        open={dialogOpen}
        pending={saveUnit.isPending}
        onCancel={() => setDialogOpen(false)}
        onSave={async (payload) => {
          try {
            await saveUnit.mutateAsync(payload);
            setDialogOpen(false);
            toast.success('Сцепка собрана.');
            return true;
          } catch (err) {
            const message =
              (err as { rawDetail?: { message?: string } })?.rawDetail?.message ??
              'Не получилось собрать сцепку — проверьте состав и попробуйте ещё раз.';
            toast.error(message);
            return false;
          }
        }}
      />
    </Stack>
  );
}

function UnitRow({
  unit,
  expanded,
  canWrite,
  onToggle,
  onOperationsChange,
}: {
  unit: MachineryUnit;
  expanded: boolean;
  canWrite: boolean;
  onToggle: () => void;
  onOperationsChange: (ids: string[]) => void;
}) {
  const norm = normLabel(unit);
  return (
    <>
      <tr className="border-b border-border/60" data-testid="unit-row">
        <td className="p-3">
          <button type="button" onClick={onToggle} aria-label="Подробности сцепки">
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
        </td>
        <td className="p-3 font-medium">{unit.name}</td>
        <td className="p-3 text-muted-foreground">{compositionLabel(unit)}</td>
        <td className="p-3">{widthLabel(unit.working_width_m)}</td>
        <td className="p-3 text-muted-foreground">
          {unit.operation_names.length ? unit.operation_names.join(' · ') : '—'}
        </td>
        <td className="p-3">
          <Badge variant={norm === 'есть' ? 'secondary' : 'outline'}>{norm}</Badge>
        </td>
      </tr>
      {expanded && (
        <tr className="border-b border-border/60 bg-muted/30">
          <td colSpan={6} className="p-4">
            <Stack gap={3}>
              {unit.has_norm ? (
                <div>
                  <p className="mb-2 text-xs uppercase text-muted-foreground">
                    Что нашлось по этому агрегату
                  </p>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-muted-foreground">
                        <th className="py-1 text-left font-medium">Операция</th>
                        <th className="py-1 text-left font-medium">Выработка за смену</th>
                        <th className="py-1 text-left font-medium">Топливо</th>
                        <th className="py-1 text-left font-medium">Источник нормы</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unit.norms.map((n, idx) => (
                        <tr key={`${n.company_op_type_id ?? 'x'}-${idx}`}>
                          <td className="py-1">
                            {n.op_name ?? n.work_name_raw ?? '—'}
                            {n.params_raw && (
                              <span className="ml-1 text-muted-foreground">({n.params_raw})</span>
                            )}
                          </td>
                          <td className="py-1">{n.norm_per_shift ?? '—'}</td>
                          <td className="py-1">{n.fuel_rate ?? '—'}</td>
                          <td className="py-1 text-muted-foreground">
                            {n.source_workbook ?? '—'}
                            {n.effective_from ? ` · с ${n.effective_from}` : ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Значения только показываются. Не устраивают — правка идёт в
                    нормативы: там у хозяйства есть своя строка, перекрывающая
                    общую. Здесь их менять нельзя, иначе у одной нормы станет два
                    значения.
                  </p>
                </div>
              ) : (
                <div>
                  <p className="mb-1 text-sm font-medium">Норматив на этот агрегат не найден</p>
                  <ul className="list-disc pl-4 text-sm text-muted-foreground">
                    {unit.missing_norm_reasons.map((reason) => (
                      <li key={reason}>{reason}</li>
                    ))}
                  </ul>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Сцепке это не мешает: агрегат существует физически независимо
                    от того, есть ли на него строка в книге. В расчёте
                    достаточности техники он будет висеть как «без нормы».
                  </p>
                </div>
              )}

              {unit.norms.length > 0 && (
                <div>
                  <p className="mb-2 text-xs uppercase text-muted-foreground">
                    Выполняемые операции
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {unit.norms
                      .filter((n) => n.company_op_type_id)
                      .map((n) => {
                        const id = n.company_op_type_id as string;
                        const checked = unit.operation_ids.includes(id);
                        return (
                          <label key={id} className="flex items-center gap-2 text-sm">
                            <Checkbox
                              checked={checked}
                              disabled={!canWrite}
                              onCheckedChange={(v) =>
                                onOperationsChange(
                                  v === true
                                    ? [...unit.operation_ids, id]
                                    : unit.operation_ids.filter((x) => x !== id),
                                )
                              }
                            />
                            {n.op_name ?? n.work_name_raw}
                          </label>
                        );
                      })}
                  </div>
                </div>
              )}
            </Stack>
          </td>
        </tr>
      )}
    </>
  );
}
